import {
  nextAvailableSnowPackName,
  type SnowPack,
  type SnowRemoval,
  type SnowSubArea,
} from "../model/inp/snowpacks.ts";
import { useT } from "../i18n/hooks.ts";
import { LayerSection, NumberField } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

const SUB_AREA_DEFAULTS: SnowSubArea = {
  cmin: 0,
  cmax: 0,
  tbase: 0,
  fwFrac: 0,
  sd0: 0,
  fw0: 0,
  snn0OrSd100: 0,
};
const REMOVAL_DEFAULTS: SnowRemoval = {
  sdPlow: 0,
  fout: 0,
  fimperv: 0,
  fperv: 0,
  fimelt: 0,
  fsubcatch: 0,
  toSubcatch: "",
};

export function SnowPacksEditor({
  packs,
  onChange,
  selectedName,
  onSelect,
}: {
  packs: SnowPack[];
  onChange: (packs: SnowPack[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deletePack,
    rename: renameSelected,
    update,
  } = useNamedCollection(packs, onChange, selectedName, onSelect);

  function addPack() {
    const name = nextAvailableSnowPackName(new Set(packs.map((p) => p.name)));
    add({ name, plowable: null, impervious: null, pervious: null, removal: null });
  }

  function patch(fields: Partial<SnowPack>) {
    update((p) => ({ ...p, ...fields }));
  }

  function subAreaFields(area: SnowSubArea, onSet: (area: SnowSubArea) => void) {
    return (
      <div className="named-collection-fields">
        <NumberField
          label={t("snow.cmin")}
          value={area.cmin}
          onChange={(v) => onSet({ ...area, cmin: v })}
        />
        <NumberField
          label={t("snow.cmax")}
          value={area.cmax}
          onChange={(v) => onSet({ ...area, cmax: v })}
        />
        <NumberField
          label={t("snow.tbase")}
          value={area.tbase}
          onChange={(v) => onSet({ ...area, tbase: v })}
        />
        <NumberField
          label={t("snow.fwFrac")}
          value={area.fwFrac}
          onChange={(v) => onSet({ ...area, fwFrac: v })}
        />
        <NumberField
          label={t("snow.sd0")}
          value={area.sd0}
          onChange={(v) => onSet({ ...area, sd0: v })}
        />
        <NumberField
          label={t("snow.fw0")}
          value={area.fw0}
          onChange={(v) => onSet({ ...area, fw0: v })}
        />
        <NumberField
          label={t("snow.snn0OrSd100")}
          value={area.snn0OrSd100}
          onChange={(v) => onSet({ ...area, snn0OrSd100: v })}
        />
      </div>
    );
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={packs}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addPack}
        addLabel={t("snow.addPack")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("snow.noPackSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deletePack(selected.name)}
              deleteLabel={t("snow.deletePack")}
            />

            <LayerSection
              title={t("snow.plowable")}
              enabled={selected.plowable !== null}
              onToggle={(on) => patch({ plowable: on ? { ...SUB_AREA_DEFAULTS } : null })}
            >
              {selected.plowable &&
                subAreaFields(selected.plowable, (area) => patch({ plowable: area }))}
            </LayerSection>

            <LayerSection
              title={t("snow.impervious")}
              enabled={selected.impervious !== null}
              onToggle={(on) => patch({ impervious: on ? { ...SUB_AREA_DEFAULTS } : null })}
            >
              {selected.impervious &&
                subAreaFields(selected.impervious, (area) => patch({ impervious: area }))}
            </LayerSection>

            <LayerSection
              title={t("snow.pervious")}
              enabled={selected.pervious !== null}
              onToggle={(on) => patch({ pervious: on ? { ...SUB_AREA_DEFAULTS } : null })}
            >
              {selected.pervious &&
                subAreaFields(selected.pervious, (area) => patch({ pervious: area }))}
            </LayerSection>

            <LayerSection
              title={t("snow.removal")}
              enabled={selected.removal !== null}
              onToggle={(on) => patch({ removal: on ? { ...REMOVAL_DEFAULTS } : null })}
            >
              {selected.removal && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("snow.sdPlow")}
                    value={selected.removal.sdPlow}
                    onChange={(v) => patch({ removal: { ...selected.removal!, sdPlow: v } })}
                  />
                  <NumberField
                    label={t("snow.fout")}
                    value={selected.removal.fout}
                    onChange={(v) => patch({ removal: { ...selected.removal!, fout: v } })}
                  />
                  <NumberField
                    label={t("snow.fimperv")}
                    value={selected.removal.fimperv}
                    onChange={(v) => patch({ removal: { ...selected.removal!, fimperv: v } })}
                  />
                  <NumberField
                    label={t("snow.fperv")}
                    value={selected.removal.fperv}
                    onChange={(v) => patch({ removal: { ...selected.removal!, fperv: v } })}
                  />
                  <NumberField
                    label={t("snow.fimelt")}
                    value={selected.removal.fimelt}
                    onChange={(v) => patch({ removal: { ...selected.removal!, fimelt: v } })}
                  />
                  <NumberField
                    label={t("snow.fsubcatch")}
                    value={selected.removal.fsubcatch}
                    onChange={(v) => patch({ removal: { ...selected.removal!, fsubcatch: v } })}
                  />
                  <label className="options-field">
                    {t("snow.toSubcatch")}
                    <input
                      type="text"
                      value={selected.removal.toSubcatch}
                      onChange={(e) =>
                        patch({ removal: { ...selected.removal!, toSubcatch: e.target.value } })
                      }
                    />
                  </label>
                </div>
              )}
            </LayerSection>
          </>
        )}
      </div>
    </div>
  );
}
